import { z } from 'zod'

const id = z.string().trim().min(1).max(200)
const text = (max: number) => z.string().trim().max(max)
const multiline = (max: number) =>
  z
    .string()
    .max(max)
    .transform((value) => value.replace(/\r\n/g, '\n').trim())

/** Table columns the sales manager can switch on or off. «№» and «Найменування» always print. */
export const PROPOSAL_COLUMNS = [
  'unit',
  'quantity',
  'ntd',
  'priceNet',
  'priceGross',
  'sumNet',
  'sumGross',
] as const
export type ProposalColumn = (typeof PROPOSAL_COLUMNS)[number]
export const PROPOSAL_COLUMN_LABELS: Record<ProposalColumn, string> = {
  unit: 'Од. вим.',
  quantity: 'Кількість',
  ntd: 'НТД',
  priceNet: 'Ціна без ПДВ',
  priceGross: 'Ціна з ПДВ',
  sumNet: 'Сума без ПДВ',
  sumGross: 'Сума з ПДВ',
}
export const proposalColumnsSchema = z
  .object({
    unit: z.boolean(),
    quantity: z.boolean(),
    ntd: z.boolean(),
    priceNet: z.boolean(),
    priceGross: z.boolean(),
    sumNet: z.boolean(),
    sumGross: z.boolean(),
  })
  .strict()

/** What the manager types about the customer's request. Prompt input, never prices. */
export const proposalBriefSchema = z
  .object({
    requestNumber: text(100),
    requestDate: z.union([z.iso.date(), z.literal('')]),
    payment: text(1000),
    delivery: text(1000),
    leadTime: text(500),
    validityDays: z.union([z.number().int().min(1).max(365), z.null()]),
  })
  .strict()
export type ProposalBrief = z.infer<typeof proposalBriefSchema>

export const proposalLetterSchema = z
  .object({
    /** Empty means «assign the next number for this company and day». */
    outgoingNumber: text(40),
    outgoingDate: z.iso.date(),
    addresseeOrganization: multiline(500),
    addresseePosition: text(300),
    addresseeName: text(300),
    title: text(200),
    intro: multiline(5000),
    conditions: multiline(10_000),
    attachments: text(1000),
    columns: proposalColumnsSchema,
    showTotals: z.boolean(),
    /** Print the saved signature and stamp images. */
    signed: z.boolean(),
    lines: z.array(z.object({ lineId: id, group: text(300), ntd: text(200) }).strict()).max(200),
    brief: proposalBriefSchema,
  })
  .strict()
export type ProposalLetter = z.infer<typeof proposalLetterSchema>

export const proposalCreateSchema = z
  .object({
    id,
    version: z.number().int().positive(),
    letter: proposalLetterSchema.optional(),
  })
  .strict()
export type ProposalCreate = z.infer<typeof proposalCreateSchema>

export const proposalTextRequestSchema = z
  .object({
    brief: proposalBriefSchema,
    addresseeOrganization: multiline(500),
    /** Pasted text of the customer's request letter. Untrusted model input. */
    requestText: multiline(20_000),
  })
  .strict()
export type ProposalTextRequest = z.infer<typeof proposalTextRequestSchema>

export interface ProposalTextResult {
  intro: string
  conditions: string
  attachments: string
  source: 'ai' | 'template'
  modelId: string | null
  /** Why the template was used, when it was. */
  notice: string | null
}

export interface ProposalLetterhead {
  sellerEntityId: string
  name: string
  legalName: string
  address: string
  phone: string
  email: string
  website: string
  edrpou: string
  altHeader: string
  signatoryPosition: string
  signatoryName: string
  logoObjectId: string | null
  signatureObjectId: string | null
  stampObjectId: string | null
}

export interface ProposalLetterLine {
  lineId: string
  name: string
  unit: string
  quantity: string
  priceNet: string
  priceGross: string
  sumNet: string
  sumGross: string
}

/** Everything the КП editor needs: defaults, the suggested number and the frozen lines. */
export interface ProposalLetterDraft {
  orderId: string
  orderVersion: number
  currencyCode: string
  vatRate: string
  letterhead: ProposalLetterhead | null
  letter: ProposalLetter
  lines: ProposalLetterLine[]
  totals: { net: string; vat: string; gross: string }
  ai: { available: boolean; modelId: string | null }
}

export const proposalLetterheadFileKinds = ['logo', 'signature', 'stamp'] as const
export type ProposalLetterheadFileKind = (typeof proposalLetterheadFileKinds)[number]
